import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Upload, X, Sparkles } from 'lucide-react';
import SearchableSelect from '../SearchableSelect';
import { useData } from '../../context/DataContext';
import { useToast } from '../../hooks/use-toast';

const UNITS = ['Piece', 'Kilogram', 'Gram', 'Litre', 'Metre', 'Square Meter', 'Box', 'Pack', 'Dozen', 'Tonne', 'Roll', 'Set', 'Pair'];

const VendorProductForm = ({ initial, onSave, onCancel, token }) => {
  const { tree } = useData();
  const { toast } = useToast();
  const isUpdate = !!initial?.id;
  const apiBase = process.env.REACT_APP_BACKEND_URL || '';

  const [v, setV] = useState({
    name: initial?.name || '',
    price: initial?.price || '',
    unit: initial?.unit || 'Piece',
    moq: initial?.moq || '1 Piece',
    images: Array.isArray(initial?.images) && initial.images.length
      ? initial.images
      : (initial?.image ? [initial.image] : []),
    description: initial?.description || '',
    main_category_id: '',
    category_id: '',
    sub_category_id: initial?.sub_category_id || '',
  });
  const [uploading, setUploading] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const set = (k, val) => setV((prev) => ({ ...prev, [k]: val }));

  const generateDescription = async () => {
    const name = (v.name || '').trim();
    if (name.length < 2) {
      toast({ title: 'Product name required', description: 'Enter a product name first, then click "Generate with AI".', variant: 'destructive' });
      return;
    }
    setAiGenerating(true);
    try {
      // Resolve the human-readable category label from the selected sub-category.
      let catLabel = '';
      for (const m of tree) {
        for (const c of (m.categories || [])) {
          const sc = (c.sub_categories || []).find((s) => s.id === v.sub_category_id);
          if (sc) { catLabel = `${m.name} → ${c.name} → ${sc.name}`; break; }
        }
        if (catLabel) break;
      }
      const resp = await fetch(`${apiBase}/api/vendor/ai/product-description`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ name, category: catLabel, keywords: '' }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      if (data?.description) {
        set('description', data.description);
        toast({ title: 'AI description generated', description: 'Feel free to tweak it before saving.' });
      }
    } catch (err) {
      toast({ title: 'AI generation failed', description: String(err?.message || err), variant: 'destructive' });
    } finally {
      setAiGenerating(false);
    }
  };

  // Reverse-resolve main + category from sub_category_id on edit
  useEffect(() => {
    if (!tree.length || !v.sub_category_id || v.category_id) return;
    for (const m of tree) {
      for (const c of (m.categories || [])) {
        if ((c.sub_categories || []).some((s) => s.id === v.sub_category_id)) {
          setV((prev) => ({ ...prev, main_category_id: m.id, category_id: c.id }));
          return;
        }
      }
    }
  }, [tree, v.sub_category_id, v.category_id]);

  const mainCategories = tree;
  const categories = (tree.find((m) => m.id === v.main_category_id)?.categories) || [];
  const subCategories = (categories.find((c) => c.id === v.category_id)?.sub_categories) || [];

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const newUrls = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      try {
        const r = await fetch(`${apiBase}/api/vendor/me/upload-image`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
          body: fd,
        });
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.detail || `Upload failed (${r.status})`);
        }
        const data = await r.json();
        newUrls.push(data.url.startsWith('http') ? data.url : `${apiBase}${data.url}`);
      } catch (err) {
        alert(`Upload of ${f.name} failed: ${err.message}`);
      }
    }
    setV((prev) => ({ ...prev, images: [...prev.images, ...newUrls] }));
    setUploading(false);
    e.target.value = '';
  };

  const removeImage = (idx) => {
    setV((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!v.sub_category_id) return alert('Please select a sub-category');
    if (!v.images.length) return alert('Please upload at least one product image');
    onSave(v);
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="vendor-product-form">
      <div className="space-y-2">
        <Label className="text-xs font-semibold text-[#2c54b2]">Category</Label>
        <div className="grid grid-cols-3 gap-2">
          <SearchableSelect
            options={mainCategories.map((m) => ({ id: m.id, name: m.name }))}
            value={v.main_category_id}
            onChange={(id) => setV((prev) => ({ ...prev, main_category_id: id, category_id: '', sub_category_id: '' }))}
            required
            placeholder="Main category"
            testId="vendor-main-cat"
          />
          <SearchableSelect
            options={categories.map((c) => ({ id: c.id, name: c.name }))}
            value={v.category_id}
            onChange={(id) => setV((prev) => ({ ...prev, category_id: id, sub_category_id: '' }))}
            required
            disabled={!v.main_category_id}
            placeholder={v.main_category_id ? 'Category' : 'Pick main first'}
            testId="vendor-cat"
          />
          <SearchableSelect
            options={subCategories.map((s) => ({ id: s.id, name: s.name }))}
            value={v.sub_category_id}
            onChange={(id) => set('sub_category_id', id)}
            required
            disabled={!v.category_id}
            placeholder={v.category_id ? 'Sub-category' : 'Pick category first'}
            testId="vendor-subcat"
          />
        </div>
      </div>

      <div>
        <Label className="text-xs">Product Name <span className="text-red-500">*</span></Label>
        <Input value={v.name} onChange={(e) => set('name', e.target.value)} required placeholder="e.g. MS Round Steel Pipe 40mm" data-testid="vendor-prod-name" />
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">Price (₹) <span className="text-red-500">*</span></Label>
          <Input type="number" min="0" step="0.01" value={v.price} onChange={(e) => set('price', e.target.value)} required />
        </div>
        <div>
          <Label className="text-xs">Unit</Label>
          <select value={v.unit} onChange={(e) => set('unit', e.target.value)} className="w-full h-10 text-sm border border-gray-300 rounded px-2 focus:outline-none focus:border-[#ee5e23]">
            {UNITS.map((u) => <option key={u}>{u}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">Min. Order Qty</Label>
          <Input value={v.moq} onChange={(e) => set('moq', e.target.value)} placeholder="e.g. 100 Piece" />
        </div>
      </div>

      <div>
        <Label className="text-xs">Product Images <span className="text-red-500">*</span></Label>
        <div className="border border-dashed border-gray-300 rounded p-3 mt-1">
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-[#2c54b2] hover:text-[#ee5e23] transition-colors">
            <Upload className="w-4 h-4" />
            <span>Upload images (you can select multiple)</span>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              multiple
              onChange={onFiles}
              className="hidden"
              data-testid="vendor-prod-images"
            />
          </label>
          {uploading && <div className="text-xs text-[#ee5e23] mt-2">Uploading…</div>}
          {v.images.length > 0 && (
            <div className="grid grid-cols-5 gap-2 mt-3">
              {v.images.map((url, i) => (
                <div key={`${url}-${i}`} className="relative group" data-testid={`vendor-prod-image-${i}`}>
                  <img src={url} alt="" className="w-full h-20 object-cover rounded border" onError={(e) => { e.target.style.opacity = '0.3'; }} />
                  {i === 0 && <span className="absolute top-1 left-1 px-1 py-0.5 text-[10px] bg-[#ee5e23] text-white rounded">Cover</span>}
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white border border-gray-300 text-gray-600 hover:bg-red-50 hover:text-red-600 flex items-center justify-center shadow"
                    title="Remove"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="text-[11px] text-gray-500 mt-2">PNG, JPG, WEBP or GIF · up to 5 MB each. The first image is used as the cover.</div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-xs">Description</Label>
          <button
            type="button"
            onClick={generateDescription}
            disabled={aiGenerating}
            className="inline-flex items-center gap-1 text-xs text-[#2c54b2] hover:text-[#ee5e23] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="vendor-prod-ai-desc"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {aiGenerating ? 'Generating…' : 'Generate with AI'}
          </button>
        </div>
        <Textarea value={v.description} onChange={(e) => set('description', e.target.value)} rows={3} placeholder="Specifications, packaging, shipping..." />
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="vendor-prod-save">
          {isUpdate ? 'Save changes' : 'Create product'}
        </Button>
      </div>
    </form>
  );
};

export default VendorProductForm;
